import memoizeOne from 'memoize-one';
import React, { Component, Fragment } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { validateHistoricProductDataViewDate } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import * as SkMaxPriceApi from '~/Api/User/ProductGroup/SkMaxPrice';
import { ErrorCode } from '~/Api/User/ProductGroup/SkMaxPrice/Error';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { formatNumericHumanDate } from '~/DateTime';
import { parsePackageSize, PricedProduct, Product, ShowAllSwitch } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import Calculation from '~/SkMaxPrice/Calculation/CalculationContainer';
import ParametersPanel, { Values as ReferenceValues } from '~/SkMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/SkMaxPrice/ProductsTable/ProductsTable';
import ActivityIndicator from '~/UI/ActivityIndicator';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';
import { Typography } from '~/libs/ui/atoms/Typography';

export interface Section {
	key: string;
	title: string;
	rows: ProductRow[];
	containsReferenceProducts: boolean;
}

interface Props {
	slovakiaProducts: ProductRow[];
	header: React.ReactNode;
	group: UserProductGroup;
	skMaxPrice: SkMaxPriceApi.SkMaxPrice | null;
	pricedProducts: PricedProduct<SkMaxPriceApi.Product>[];
	validationResult: SkMaxPriceApi.ValidationResult | null;
	isFetching: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	currency: string | null;
	referencedProducts: ProductRow[];
	referenceBasketVersion: ReferenceBasketVersion | null;
	otherProducts: ProductRow[];
	alternativeProductsCount: number;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(productId: string, tabName: string): void;
	onUpdate(group: UserProductGroup, update: SkMaxPriceApi.Update): void;
	onSave(group: UserProductGroup): void;
	onCurrencyChange(currency: string): void;
	onDeleteItems(group: UserProductGroup, products: Product[], onSuccess: () => void): Promise<void>;
}

interface Values {
	reference: ReferenceValues;
	product: { [id: string]: ProductValues };
}

interface State {
	values: Values | null;
	lastSelectedProductId: string | null;
	lastSeenProductIdDetail: string | null;
	removeProduct: Product | null;
	checkedRows: Product[];
}

export default class SkMaxPrice extends Component<Props, State> {
	static defaultProps = {
		skMaxPrice: null,
		validationResult: null,
		detailProductId: null,
		detailProductTabName: 'info',
	};

	state: State = {
		values: null,
		lastSelectedProductId: null,
		lastSeenProductIdDetail: null,
		removeProduct: null,
		checkedRows: [],
	};

	componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	resolveValues(): Values {
		const {
			props: { skMaxPrice },
			state: { values },
		} = this;
		return this.createValues(skMaxPrice, values);
	}

	private readonly checkRow = (product: Product, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: Product[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	createValues = memoizeOne((skMaxPrice: SkMaxPriceApi.SkMaxPrice | null, values: Values | null): Values => {
		const reference = skMaxPrice?.reference;
		const historicProductDataViewDate = skMaxPrice?.reference.historicProductDataViewDate ?? null;
		const priceComparisonFxDate = skMaxPrice?.reference.priceComparisonFxDate ?? null;

		return {
			reference: values?.reference ?? {
				basketVersion: skMaxPrice?.reference.referenceBasketVersion ?? '',
				packageSize: {
					value: String(reference?.packageSize ?? ''),
					isValid: true,
				},
				historicProductDataViewDate: {
					value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
					dateValue: historicProductDataViewDate,
					isValid: true,
				},
				priceComparisonFxDate: {
					value: priceComparisonFxDate !== null ? formatNumericHumanDate(priceComparisonFxDate) : '',
					dateValue: priceComparisonFxDate,
					isValid: true,
				},
			},
			product:
				skMaxPrice?.products.reduce((products: { [id: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					const hasSelectedValidReferencePrice =
						currentValues !== undefined &&
						product.referencePrices.find(
							(referencePrice) => referencePrice.calculatedPrice.type === currentValues.referencePrice.value?.value
						) !== undefined;
					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

					products[product.product.id] = {
						packageSize: currentValues?.packageSize ?? {
							isValid: true,
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						},
						referencePrice:
							currentValues !== undefined && hasSelectedValidReferencePrice
								? currentValues.referencePrice
								: {
										value:
											referencePrice !== undefined
												? {
														value: referencePrice.calculatedPrice.type,
														parentValue: null,
														prompt: null,
												  }
												: null,
										isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
								  },
						packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
							isValid: true,
						},
						selected: currentValues?.selected ?? product.selected,
					};
					return products;
				}, {}) ?? {},
		};
	});

	processValidationResult(validationResult: SkMaxPriceApi.ValidationResult | null) {
		if (!validationResult) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referenceSettingsIsMissing,
			},
		};
		const productValues = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			if (error.productId === null) {
				return;
			}

			const values = productValues[error.productId];
			if (!values) {
				return;
			}
			if (error.code === ErrorCode.RequiredProductSettingsMissingErrorCode) {
				productValues[error.productId] = {
					...values,
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.IllegalPriceTypeSelectedErrorCode) {
				productValues[error.productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		this.setState({ values: { ...values, reference } }, this.update);
	};

	handleProductValuesChange = (
		item: SkMaxPriceApi.Product,
		referencePrice: ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice.value !== referencePrice;

		this.setState(
			{
				values: {
					reference: values.reference,
					product: {
						...values.product,
						[item.product.id]: {
							referencePrice: {
								value: referencePrice,
								isValid: item.referencePrices.length === 0 || referencePrice !== null,
							},
							packageSize: { value: packageSize, isValid: null },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							selected: currentValue.selected,
						},
					},
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	productSelected = (item: SkMaxPriceApi.Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				values: { reference: values.reference, product: productValues },
				lastSelectedProductId: item.product.id,
			},
			this.update
		);
	};

	selectProductRange(
		productValues: { [id: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		referenceValues.historicProductDataViewDate.isValid =
			referenceValues.historicProductDataViewDate.value === '' ||
			(referenceValues.historicProductDataViewDate.dateValue !== null &&
				validateHistoricProductDataViewDate(referenceValues.historicProductDataViewDate.dateValue));

		let hasValidationError =
			referencePackageSize.isValid === false || referenceValues.historicProductDataViewDate.isValid === false;

		const products: SkMaxPriceApi.ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit } = values;
			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			hasValidationError =
				hasValidationError || !referencePrice.isValid || parsedPackageSizeForSingleUnit.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
			};

			products.push(
				new SkMaxPriceApi.ProductUpdate(
					productId,
					referencePrice.value?.value ?? null,
					parsedPackageSize.value,
					parsedPackageSizeForSingleUnit.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(this.props.group, {
				packageSize: referencePackageSize.value,
				historicProductDataViewDate: referenceValues.historicProductDataViewDate.dateValue,
				products,
				referenceBasketVersion: referenceValues.basketVersion,
				priceComparisonFxDate: referenceValues.priceComparisonFxDate.dateValue,
			});
		}
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	render() {
		const {
			props: {
				header,
				group,
				skMaxPrice,
				referenceBasketVersion,
				pricedProducts,
				slovakiaProducts,
				referencedProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				alternativeProductsCount,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		if (skMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider id={detailProductId} skMaxPrice={skMaxPrice}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => onShowDetail(productId, detailProductTabName)}
										onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{skMaxPrice !== null && referenceBasketVersion !== null ? (
						<Calculation
							skMaxPrice={skMaxPrice}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
						/>
					) : null}

					<ParametersPanel
						group={group}
						values={values.reference}
						onChange={this.referenceValuesChanged}
						onSave={() => onSave(group)}
					/>

					{skMaxPrice?.products.length === 0 ? <ProductInstructions /> : null}

					{(skMaxPrice?.products.length || 0) > 0 ? (
						<>
							<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
								<Typography level={2} weight='font-light' className='pr-4'>
									Slovak products
								</Typography>
							</div>
							<ProductsTable
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								historicData={Boolean(skMaxPrice?.reference.historicProductDataViewDate)}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								rows={slovakiaProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={this.handleProductValuesChange}
								onSave={() => {
									onSave(group);
								}}
								onSelect={this.productSelected}
							/>
							<div className='max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-wrap justify-between items-end'>
								<Typography level={2} weight='font-light' className='pr-4'>
									Reference basket products
								</Typography>
								<div className='relative w-auto min-h-7 grow shrink pt-1 pb-0.5'>
									<ShowAllSwitch alternativeProductsCount={alternativeProductsCount} />
								</div>
							</div>
						</>
					) : null}

					{referencedProducts.length > 0 ? (
						<ProductsTable
							showReferenceDataSource
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(skMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							rows={referencedProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{otherProducts.length > 0 ? (
						<>
							<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
								<Typography level={2} weight='font-light' className='pr-4'>
									Other
								</Typography>
							</div>
							<ProductsTable
								disableSelection
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								historicData={Boolean(skMaxPrice?.reference.historicProductDataViewDate)}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								rows={otherProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={this.handleProductValuesChange}
								onSave={() => {
									onSave(group);
								}}
								onSelect={this.productSelected}
							/>
						</>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => this.setState({ removeProduct: null })}
							onConfirm={async () => onSave(group)}
							onSuccess={() => this.setState({ removeProduct: null })}
						/>
					) : null}
					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}
}
